import { Plus } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { SearchInput } from '../../../components/Input';
import { fuzzyFilter } from '../../../lib/fuzzy';
import styles from './ChoosePicker.module.css';

export interface Choice {
  readonly ids: readonly string[];
  readonly newNames: readonly string[];
}

interface ChoosePickerProps {
  readonly title: string;
  readonly subtitle: string;
  /** "pool" / "tag" — used in the placeholder and the "new" hint. */
  readonly noun: string;
  readonly options: readonly { readonly id: string; readonly name: string }[];
  readonly confirmLabel: string;
  readonly onConfirm: (choice: Choice) => void;
}

/**
 * Tick the ones that exist, or type a name and press Enter to make a new one — pools
 * (S15 Q3) and tags (S16 Q2) alike. The confirm stays disabled until something is
 * chosen, and says what is missing beside it.
 */
export function ChoosePicker({ title, subtitle, noun, options, confirmLabel, onConfirm }: ChoosePickerProps): ReactNode {
  const [query, setQuery] = useState('');
  const [ids, setIds] = useState<readonly string[]>([]);
  const [newNames, setNewNames] = useState<readonly string[]>([]);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // A popover is hidden for its first frame while it is placed; focus goes in on the next.
  useEffect(() => {
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  const trimmed = query.trim().replace(/\s+/g, ' ');
  const shown = fuzzyFilter(query, options, (option) => option.name).map((result) => result.item);
  const isKnown = (name: string): boolean =>
    options.some((option) => option.name.toLowerCase() === name.toLowerCase()) || newNames.some((other) => other.toLowerCase() === name.toLowerCase());
  const canCreate = trimmed !== '' && !isKnown(trimmed);
  const count = ids.length + newNames.length;

  /** Enter on a name: tick it if it exists (whatever its case), make it if it does not. */
  function pick(): void {
    const existing = options.find((option) => option.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      setIds((current) => (current.includes(existing.id) ? current : [...current, existing.id]));
      setQuery('');
      return;
    }
    if (!canCreate) return;
    setNewNames((current) => [...current, trimmed]);
    setQuery('');
  }

  return (
    <div className={styles.picker}>
      <p className={styles.title}>
        {title}
        <span className={styles.subtitle}>{subtitle}</span>
      </p>
      <SearchInput
        ref={searchRef}
        value={query}
        placeholder={`Find or name a ${noun}`}
        aria-label={`Find or name a ${noun}`}
        onChange={(event) => setQuery(event.target.value)}
        onClear={() => setQuery('')}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          if (trimmed !== '') pick();
          else if (count > 0) onConfirm({ ids, newNames });
        }}
      />
      <ul className={styles.list} aria-label={`${noun}s`}>
        {newNames.map((name) => (
          <li key={`new-${name}`}>
            <Checkbox checked onChange={() => setNewNames((current) => current.filter((other) => other !== name))} label={`${name} (new)`} />
          </li>
        ))}
        {shown.map((option) => (
          <li key={option.id}>
            <Checkbox
              checked={ids.includes(option.id)}
              onChange={() => setIds((current) => (current.includes(option.id) ? current.filter((id) => id !== option.id) : [...current, option.id]))}
              label={option.name}
            />
          </li>
        ))}
        {canCreate && (
          <li>
            <button type="button" className={styles.create} onClick={pick}>
              <Plus size={14} aria-hidden />
              New {noun} “{trimmed}”
            </button>
          </li>
        )}
        {shown.length === 0 && !canCreate && newNames.length === 0 && <li className={styles.empty}>No {noun}s yet — type a name to make one.</li>}
      </ul>
      <div className={styles.footer}>
        {count === 0 && <span className={styles.problem}>Choose at least one {noun}.</span>}
        <Button variant="solid" disabled={count === 0} onClick={() => onConfirm({ ids, newNames })}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
